<template>
  <AppSkeletonCardTable v-if="loading"></AppSkeletonCardTable>
  <Card v-else style="min-height: 494px">
    <template v-slot:title>
      <div class="p-grid p-jc-between">
        <div class="p-col-12 p-md-6">
          Bovinos
          <router-link
            v-if="lotId"
            :to="{ name: 'bovine.list', query: { lotId: lotId } }"
            target="_blank"
          >
            <br class="hide mobile-show" />
            <span style="font-size: 1rem; font-weight: normal">
              (Listagem de Bovinos do lote/piquete)
            </span>
          </router-link>
        </div>
        <div class="p-col" style="text-align: right">
          <span
            style="display-inline-block"
            v-tooltip.top="
              !lotId ? 'Salve o lote/piquete para adicionar bovinos.' : ''
            "
          >
            <Button
              :disabled="!lotId"
              label="Adicionar Bovinos"
              class="p-button-outlined"
              @click="selectorVisible = true"
            />
          </span>
        </div>
      </div>
    </template>
    <template v-slot:content>
      <DataTable
        dataKey="id"
        responsiveLayout="stack"
        :value="results"
        :scrollHeight="$isLargeScreen.value ? '500px' : undefined"
        :scrollable="$isLargeScreen.value"
        @sort="onSort($event)"
      >
        <template #header>
          <span class="p-input-icon-left">
            <i class="pi pi-search" />
            <InputText
              v-model="filters.term"
              placeholder="Buscar bovinos"
              @input="search()"
            />
          </span>
        </template>
        <Column
          header="Brinco"
          field="earring"
          :sortable="true"
          style="min-width: 90px"
          alignFrozen="left"
          :frozen="true"
        >
          <template #body="{ data: { earring, id } }">
            <router-link
              :to="{ name: 'bovine.edit', params: { id } }"
              target="_blank"
              style="text-decoration: none"
            >
              {{ earring }}
            </router-link>
          </template>
        </Column>
        <Column
          header="Tipo"
          field="type.name"
          :sortable="true"
          style="min-width: 90px"
          alignFrozen="left"
          :frozen="true"
        >
          <template #body="{ data: { type, id } }">
            <router-link
              :to="{ name: 'bovine.edit', params: { id } }"
              target="_blank"
              style="text-decoration: none"
            >
              <BovineType :type="type" small />
            </router-link>
          </template>
        </Column>
        <Column
          header="Raça"
          :sortable="true"
          style="min-width: 80px"
          field="breed.name"
        >
          <template #body="{ data: bovine }">
            {{ bovine?.breed?.name || '-' }}
          </template>
        </Column>
        <Column
          header="Data de nascimento"
          field="birthDate"
          :sortable="true"
          style="min-width: 100px"
        >
          <template #body="slotProps">
            {{
              slotProps.data.birthDate
                ? $dayjs(slotProps.data.birthDate).format('DD/MM/YYYY')
                : '-'
            }}
          </template>
        </Column>
        <Column
          header="Peso"
          field="weight"
          :sortable="true"
          style="min-width: 90px"
        >
          <template #body="{ data: { weight } }">
            {{ weight ? $toDecimal(weight) : '-' }}
          </template>
        </Column>
        <Column
          header="Status"
          field="status.name"
          :sortable="true"
          style="min-width: 90px"
        >
          <template #body="slotProps">
            {{ slotProps.data?.status?.name || '-' }}
          </template>
        </Column>
        <Column
          header="Matriz"
          :sortable="true"
          style="min-width: 90px"
          field="bovineRoot.earring"
        >
          <template #body="{ data: bovine }">
            <span v-if="bovine?.bovineRoot?.id">
              <router-link
                :to="{
                  name: 'bovine.edit',
                  params: { id: bovine?.bovineRoot?.id },
                }"
                target="_blank"
                style="text-decoration: none"
              >
                {{ bovine?.bovineRoot?.earring }}
              </router-link>
            </span>
            <span v-else> - </span>
          </template>
        </Column>
        <Column
          header="Proprietário"
          field="owner.name"
          :sortable="true"
          style="min-width: 120px"
        >
          <template #body="{ data }">
            <span v-if="data?.owner?.id">
              <router-link
                :to="{
                  name: 'owner.edit',
                  params: { id: data?.owner?.id },
                }"
                target="_blank"
                style="text-decoration: none"
                >{{ $sliceText(data?.owner?.name, 15) }}</router-link
              >
            </span>
            <span v-else> - </span>
          </template>
        </Column>
        <Column
          header="Ações"
          style="min-width: 80px"
          alignFrozen="right"
          :frozen="true"
        >
          <template #body="{ data: { id } }">
            <Button
              type="button"
              icon="pi pi-times"
              class="p-button-text p-button-secondary"
              @click="removeBovine(id)"
            ></Button>
          </template>
        </Column>
      </DataTable>

      <Paginator
        :first="query.pageSize * pagination.current - 1"
        :rows="query.pageSize"
        :totalRecords="pagination.total"
        @page="changeListPage({ page: $event.page + 1 })"
      ></Paginator>

      <BovineSelector
        v-model:visible="selectorVisible"
        @select="onBovineSelect($event)"
        :defaultQuery="
          lotId ? { excludeLotId: lotId, available: true } : { available: true }
        "
        :showSelectAll="true"
      ></BovineSelector>
    </template>
  </Card>
</template>

<script>
import debounce from 'debounce';
import { BovineApi, FarmLotApi } from '../../../services/api';
import BovineType from '../../../components/BovineType';
import BovineSelector from '../../../components/BovineSelector';

export default {
  name: 'ServiceBovineCard',
  props: {
    lotId: Number,
    bovines: Array,
  },
  emits: ['save'],
  components: {
    BovineType,
    BovineSelector,
  },
  async created() {
    await this.fetchList();
  },
  watch: {
    lotId() {
      this.query.lotId = this.lotId;
      this.fetchList();
    },
  },
  data() {
    return {
      filters: { term: '' },
      selectorVisible: false,
      results: [],
      loading: false,
      query: {
        lotId: this.lotId || undefined,
        page: 1,
        pageSize: 20,
        order: 'desc',
        orderField: 'updatedAt',
        available: true,
      },
      pagination: {
        total: 0,
        current: 1,
        pages: 1,
      },
    };
  },
  methods: {
    async onSort(event) {
      this.query.order = event.sortOrder === 1 ? 'asc' : 'desc';
      this.query.orderField = event.sortField;
      this.fetchList();
    },
    async fetchList() {
      if (!this.lotId) {
        return;
      }
      try {
        this.loading = true;
        const fetchResponse = await BovineApi.fetch(this.query);
        this.results = fetchResponse.results;
        this.pagination = fetchResponse.pagination;
      } finally {
        this.loading = false;
      }
    },
    search: debounce(async function search() {
      this.query.term = this.filters.term;
      this.fetchList();
    }, 500),
    async changeListPage({ page }) {
      this.query.page = page;
      await this.fetchList();
    },
    async onBovineSelect({ values }) {
      const bovines = values.map(({ id }) => ({ id, status: true }));
      if (this.lotId) {
        await FarmLotApi.patch(this.lotId, { bovines });
        await this.fetchList();
      } else {
        this.$emit('update:bovines', bovines);
        this.$emit('save');
      }
    },
    async removeBovine(id) {
      if (this.lotId) {
        await FarmLotApi.patch(this.lotId, {
          bovines: [{ id, status: false }],
        });
      }
      this.results = this.results.filter((result) => result.id !== id);
      if (!this.results.length) {
        this.query.page = this.query.page - 1 || 1;
        await this.fetchList();
      }
    },
  },
};
</script>

<style scoped>
</style>
